import { createContext, useContext, useEffect, useState } from 'react';

const THEMES = ['dark', 'light', 'system'] as const;

type Theme = (typeof THEMES)[number];
type AppliedTheme = Exclude<Theme, 'system'>;

const isTheme = (value: unknown): value is Theme => THEMES.includes(value as Theme);

const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  appliedTheme: AppliedTheme;
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  appliedTheme: 'light',
  theme: 'system',
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'vite-ui-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    const storageTheme = localStorage.getItem(storageKey);
    return isTheme(storageTheme) ? storageTheme : defaultTheme;
  });
  const [systemTheme, setSystemTheme] = useState<AppliedTheme>(() =>
    window.matchMedia(SYSTEM_DARK_QUERY).matches ? 'dark' : 'light',
  );

  useEffect(() => {
    const mediaQueryList = window.matchMedia(SYSTEM_DARK_QUERY);
    const syncSystemTheme = () => {
      setSystemTheme(mediaQueryList.matches ? 'dark' : 'light');
    };

    mediaQueryList.addEventListener('change', syncSystemTheme);
    return () => {
      mediaQueryList.removeEventListener('change', syncSystemTheme);
    };
  }, []);

  const appliedTheme = theme === 'system' ? systemTheme : theme;

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove('light', 'dark');
    root.classList.add(appliedTheme);
  }, [appliedTheme]);

  const value = {
    appliedTheme,
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) throw new Error('useTheme must be used within a ThemeProvider');

  return context;
};
